import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

// OrderedListField — the DS control for a form value whose ORDER is part of its
// meaning (story 3-3b; first consumer: an inspection type's `severityScale`,
// where "Low → Medium → High" is a scale, not a set).
//
// WHY A NEW PRIMITIVE, and why exactly one. `MultiSelect` (2-96) edits an
// unordered `string[]` from a fixed option list; `DynamicFormField` renders one
// scalar control per FormSchema v2 field. Neither can express "a list of
// composite rows the user adds, removes and REORDERS", and a scale editor that
// cannot reorder is not a scale editor. So the row's *contents* stay the
// caller's business (`renderRow` composes ordinary DS controls) and this
// primitive owns only the list mechanics — add, remove, move, and the
// accessibility that reordering demands.
//
// ── ACCESSIBILITY (P10 §2, D44) ───────────────────────────────────────────
//  * `<ol>`/`<li>`: position-in-set is native list semantics, so a screen
//    reader announces "3 of 5" without any aria-posinset bookkeeping that
//    could drift from the DOM.
//  * Every control is a real `<button>` (the DS `Button`), so the whole
//    control is keyboard-operable with Tab + Enter/Space and inherits
//    `focus-ring`. No drag-and-drop-only affordance: DnD without a keyboard
//    equivalent is the classic reorder-control a11y failure, so this
//    primitive ships the keyboard path as the ONLY path rather than as a
//    fallback nobody tests.
//  * Accessible names are ROW-QUALIFIED — "Move High up", "Remove High" —
//    because five identical "Move up" buttons are indistinguishable in a
//    screen-reader element list.
//  * The first row's "move up" and the last row's "move down" are DISABLED
//    rather than absent: a control that disappears mid-interaction moves
//    focus somewhere unexpected.
//  * Reordering mutates DOM order silently, so the move is ANNOUNCED through
//    a polite live region ("High moved to position 2 of 4") and focus follows
//    the row that moved — onto the same button at its new index, or, when that
//    button is the one the boundary disables, onto the opposite-direction
//    button of the SAME row (see `move` below). That is what makes repeated
//    presses keep acting on the item the user is actually moving.

export interface OrderedListFieldRowProps<T> {
  item: T;
  index: number;
  /** Merge a partial into THIS row (the caller never rebuilds the array). */
  patch: (partial: Partial<T>) => void;
  disabled: boolean;
  /** Stable id prefix for the row's own controls (label/aria wiring). */
  rowId: string;
}

export interface OrderedListFieldProps<T> {
  id?: string;
  name: string;
  value: readonly T[] | undefined;
  onChange: (next: T[]) => void;
  onBlur?: () => void;
  /** The row body — compose DS controls here. */
  renderRow: (props: OrderedListFieldRowProps<T>) => ReactNode;
  /** Human name for one row; used in every control's accessible name. */
  rowLabel: (item: T, index: number) => string;
  /** Factory for the row the "add" button appends. */
  createItem: () => T;
  addLabel: string;
  /** Shown in place of the list when there are no rows. */
  emptyMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Inline error text, id-linked to the list via aria-describedby. */
  errorMessage?: string;
  /** Hard cap on rows; the add button disables at the cap. */
  max?: number;
}

export function OrderedListField<T>({
  id,
  name,
  value,
  onChange,
  onBlur,
  renderRow,
  rowLabel,
  createItem,
  addLabel,
  emptyMessage = 'Nothing added yet.',
  disabled = false,
  invalid = false,
  errorMessage,
  max,
}: OrderedListFieldProps<T>): JSX.Element {
  const items = value ? [...value] : [];
  const baseId = id ?? name;
  const errorId = errorMessage ? `${baseId}-error` : undefined;

  // Announcement for the polite live region — see the a11y note above.
  const [announcement, setAnnouncement] = useState('');
  // Which move button should receive focus after the next commit, as
  // `${index}:${direction}`. A ref map (stable across renders) holds the DOM
  // nodes, so the effect below depends only on the target.
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [focusTarget, setFocusTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!focusTarget) return;
    buttonRefs.current[focusTarget]?.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const commit = (next: T[]) => {
    onChange(next);
    onBlur?.();
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    const item = items[index];
    if (target < 0 || target >= items.length || item === undefined) return;
    const next = [...items];
    next.splice(index, 1);
    next.splice(target, 0, item);
    commit(next);
    setAnnouncement(`${rowLabel(item, target)} moved to position ${target + 1} of ${next.length}`);
    // Focus follows the MOVED ROW, onto a control that is still operable there.
    // Naively re-focusing the same direction breaks at the two boundaries: the
    // first row's "move up" and the last row's "move down" are disabled, and a
    // disabled button cannot take focus — so focus would silently stay on the
    // DOM node that is now a DIFFERENT row, and the next keypress would move
    // the wrong item. At a boundary the opposite-direction button of the same
    // row is used instead: still the row the user just moved, still one Enter
    // away from moving it back.
    const forward = delta < 0 ? 'up' : 'down';
    const stillEnabled = delta < 0 ? target > 0 : target < next.length - 1;
    setFocusTarget(`${target}:${stillEnabled ? forward : forward === 'up' ? 'down' : 'up'}`);
  };

  const remove = (index: number) => {
    const item = items[index];
    if (item === undefined) return;
    const next = items.filter((_, i) => i !== index);
    commit(next);
    setAnnouncement(`${rowLabel(item, index)} removed. ${next.length} remaining.`);
  };

  const add = () => {
    const next = [...items, createItem()];
    commit(next);
    setAnnouncement(`Row ${next.length} added.`);
  };

  const atMax = typeof max === 'number' && items.length >= max;

  return (
    <div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <ol
          id={baseId}
          data-testid={`${baseId}-list`}
          className="space-y-2"
          {...(errorId ? { 'aria-describedby': errorId } : {})}
          {...(invalid ? { 'aria-invalid': true } : {})}
        >
          {items.map((item, index) => {
            const label = rowLabel(item, index);
            const rowId = `${baseId}-${index}`;
            const patch = (partial: Partial<T>) => {
              const next = [...items];
              next[index] = { ...(item as object), ...(partial as object) } as T;
              commit(next);
            };
            return (
              // Keyed by POSITION on purpose: a row has no identity until the
              // user names it, and position IS the value being edited here —
              // keying by content would remount every row on every keystroke.
              <li key={rowId} data-testid={`${baseId}-row`} className="rounded-xl border border-border p-3">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-0 flex-1">{renderRow({ item, index, patch, disabled, rowId })}</div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      ref={(node) => {
                        buttonRefs.current[`${index}:up`] = node;
                      }}
                      variant="ghost"
                      size="sm"
                      iconOnly
                      icon={<ArrowUpIcon className="h-4 w-4" />}
                      disabled={disabled || index === 0}
                      onClick={() => move(index, -1)}
                    >
                      {`Move ${label} up`}
                    </Button>
                    <Button
                      ref={(node) => {
                        buttonRefs.current[`${index}:down`] = node;
                      }}
                      variant="ghost"
                      size="sm"
                      iconOnly
                      icon={<ArrowDownIcon className="h-4 w-4" />}
                      disabled={disabled || index === items.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      {`Move ${label} down`}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      icon={<TrashIcon className="h-4 w-4" />}
                      disabled={disabled}
                      onClick={() => remove(index)}
                    >
                      {`Remove ${label}`}
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <div className="mt-2 flex items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          icon={<PlusIcon className="h-4 w-4" />}
          disabled={disabled || atMax}
          onClick={add}
          data-testid={`${baseId}-add`}
        >
          {addLabel}
        </Button>
        {atMax && <span className="text-xs text-muted-foreground">{`Maximum of ${max} reached.`}</span>}
      </div>

      {invalid && errorMessage && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-destructive">
          {errorMessage}
        </p>
      )}

      {/* Reordering is silent to assistive tech without this. */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  );
}
